import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import useGetAgent from '@/hooks/useGetAgent';
import useGetProfile from '@/hooks/useGetProfile';
import useRequestHelp from '@/hooks/useRequestHelp';
import { useAuth0 } from '@auth0/auth0-react';
import { HelpCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AddressAutocomplete from './AddressAutocomplete';
import RegisterAccountModal from './RegisterAccountModa';
import PhoneInput from './ui/phoneInput';
import TutorialModal from './ui/TutorialModal';

interface GetHelpFormData {
  name: string;
  email: string;
  phone: string;
  propertyAddress: string;
  helpNeeded: string[];
  additionalInfo: string;
  propertyId: string;
}

const helpOptions = [
  'Picking comparable sales',
  'How much of an adjustment to make',
  'Appeal procedures',
  'Technical issues',
  'Other',
];

export const GetHelpButton = () => {
  const [query] = useSearchParams();
  const { data: user } = useGetProfile();
  const { mutate: requestHelp, isPending: requestPending } = useRequestHelp();
  const { isAuthenticated } = useAuth0();

  const [isOpen, setIsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [isOpenTutorial, setIsOpenTutorial] = useState(false);
  const [formData, setFormData] = useState<GetHelpFormData>({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyId: '',
    helpNeeded: [],
    additionalInfo: '',
  });

  const agentIdentifier = useMemo(() => {
    const agent = query.get('agent');
    if (agent) window.localStorage.setItem('agent', agent.replace(' ', '+'));
    const localAgent = window.localStorage.getItem('agent');
    return localAgent;
  }, [query]);

  const { data: _agent } = useGetAgent(user?.agent ? null : agentIdentifier);

  const agent = useMemo(
    () =>
      user?.agent ||
      _agent || {
        emailAddress: 'Support@PropertyTaxHero.com',
        phoneNumber: '303-554-7652',
        firstName: 'PropertyTaxHero',
        lastName: 'Support',
      },
    [_agent, user?.agent],
  );

  useEffect(() => {
    if (user) {
      setFormData({
        name: `${user?.firstName} ${user?.lastName}`,
        email: user?.emailAddress,
        phone: user?.phoneNumber,
        propertyAddress: '',
        helpNeeded: [],
        additionalInfo: '',
        propertyId: '',
      });
    }
  }, [user]);

  const handleCheckboxChange = (option: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      helpNeeded: checked
        ? [...prev.helpNeeded, option]
        : prev.helpNeeded.filter((item) => item !== option),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!agent) return;

    requestHelp(
      {
        agentEmailAddress: agent.emailAddress,
        agentName: `${agent?.firstName} ${agent?.lastName}`,
        clientEmailAddress: formData.email,
        clientName: formData.name,
        helpNeeded: formData.helpNeeded,
        clientPhoneNumber: formData.phone,
        propertyAddress: formData.propertyAddress,
        propertyId: formData.propertyId,
        additionalInfo: formData.additionalInfo,
      },
      {
        onSuccess: () => {
          setFormData({
            name: `${user?.firstName} ${user?.lastName}`,
            email: user?.emailAddress,
            phone: user?.phoneNumber,
            propertyAddress: '',
            helpNeeded: [],
            additionalInfo: '',
            propertyId: '',
          });
          setOpen(false);
        },
      },
    );
  };

  if (!agent) return null;

  return !isAuthenticated ? (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        variant="outline"
        size="sm"
        className="bg-warning text-warning-foreground border-warning hover:bg-warning/90 hover:border-warning/90 gap-2 text-sm font-medium shadow-sm w-full md:w-fit"
      >
        <HelpCircle size={16} />
        Get Help
      </Button>
      <RegisterAccountModal
        isOpen={isOpen}
        setIsOpen={() => setIsOpen(false)}
      />
    </>
  ) : (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-warning text-warning-foreground border-warning hover:bg-warning/90 hover:border-warning/90 gap-2 text-sm font-medium shadow-sm w-full md:w-fit"
          >
            <HelpCircle size={16} />
            Get Help
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 sm:w-96 max-h-[80vh] overflow-y-auto p-6"
          align="end"
          side="bottom"
        >
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground leading-relaxed">
              <strong className="capitalize">
                {agent?.firstName} {agent?.lastName}
              </strong>{' '}
              is eager to help you. As a local real estate professional they
              know the market as well as anyone and are a valuable resource.{' '}
              <span className="capitalize">
                {agent?.firstName} {agent?.lastName}
              </span>
              's email is <strong>{agent?.emailAddress}</strong> and their cell
              phone is <strong>{agent?.phoneNumber}</strong> if you need
              immediate help. Otherwise we'll forward them the following
              information.
              <br />
              <br />
              <button
                onClick={() => setIsOpenTutorial(true)}
                className="text-tax-deepBlue"
              >
                Watch a video tutorial.
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Your name:
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter your name"
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Your email:
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="Enter your email"
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Your phone:
                  </Label>
                  <PhoneInput
                    value={formData.phone}
                    name="phone"
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, phone: val }))
                    }
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <Label
                    htmlFor="propertyAddress"
                    className="text-sm font-medium"
                  >
                    Property address:
                  </Label>
                  <AddressAutocomplete
                    inputOnly
                    isLoading={false}
                    onSearch={(address, id) => {
                      setFormData((prev) => ({
                        ...prev,
                        propertyAddress: address,
                        propertyId: id,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  What do you need help with:
                </Label>
                <div className="space-y-2">
                  {helpOptions.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <Checkbox
                        id={option}
                        checked={formData.helpNeeded.includes(option)}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(option, checked as boolean)
                        }
                      />
                      <Label
                        htmlFor={option}
                        className="text-sm leading-none cursor-pointer"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="additionalInfo" className="text-sm font-medium">
                  Tell us more:
                </Label>
                <Textarea
                  id="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      additionalInfo: e.target.value,
                    }))
                  }
                  placeholder="Please provide additional details..."
                  className="min-h-[80px] text-sm resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="bg-warning text-warning-foreground hover:bg-warning/90"
                  disabled={
                    !formData.propertyAddress ||
                    !formData.propertyId ||
                    requestPending
                  }
                >
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </PopoverContent>
      </Popover>
      <TutorialModal
        isOpen={isOpenTutorial}
        setIsOpen={() => setIsOpenTutorial(false)}
      />
    </>
  );
};
